import { ConfigProvider, Button, Form, Input, InputNumber, message, Modal, Select } from "antd";
import { Dispatch, SetStateAction, useEffect, useState } from "react";
import container from "@/lib/di/container";
import { ElectronicContractRepo } from "@/core/repos/tables/electronic-contract.repo";
import React from "react";
import { MODAL_THEME } from "@/core/themes/modal.theme";
import { SignDocumentModel } from "@/core/models/electronic-contract.model";

const electronicContractRepo = container.get(ElectronicContractRepo);

interface SignDocumentModalProps {
    isModalOpen: boolean,
    setIsModalOpen: Dispatch<SetStateAction<boolean>>,
    contractId: string,
    onSuccess?: () => void,
}

const INPUT_WIDTH = 450;

export const SignDocumentModal: React.FC<SignDocumentModalProps> = ({isModalOpen, setIsModalOpen, contractId, onSuccess}) => {
    const [form] = Form.useForm<SignDocumentModel>();
    const [loading, setLoading] = useState<boolean>(false);
    const [messageApi, contextHolder] = message.useMessage();

    useEffect(() => {
        if (isModalOpen) {
            form.setFieldsValue({
                electronicContractId: contractId,
                otpType: 1,
                deviceOTP: "",
                codeOTP: "",
                signatureCustomerImage64: ""
            });
        }
    }, [isModalOpen, contractId, form]);

    const handleSign = async () => {
        try {
            setLoading(true);
            const formData = await form.validateFields();
            const result = await electronicContractRepo.signDocument({
                electronicContractId: contractId,
                otpType: formData.otpType,
                deviceOTP: formData.deviceOTP,
                codeOTP: formData.codeOTP,
                signatureCustomerImage64: formData.signatureCustomerImage64
            });
            
            if (result) {
                messageApi.success({
                    content: "Ký hợp đồng thành công"
                });
                setIsModalOpen(false);
                if (onSuccess) {
                    onSuccess();
                }
            } else {
                messageApi.error({
                    content: "Ký hợp đồng thất bại. Vui lòng kiểm tra lại thông tin"
                });
            }
        } catch (error) {
            console.log(error);
            messageApi.error({
                content: "Lỗi khi ký hợp đồng"
            });
        } finally {
            setLoading(false);
        }
    };

    const handleCancel = () => {
        setIsModalOpen(false);
    };

    return (
        <ConfigProvider theme={MODAL_THEME}>
            {contextHolder}
            <Modal 
                open={isModalOpen} 
                onCancel={handleCancel} 
                width={600} 
                centered
                styles={{
                    mask: {
                        backdropFilter: 'blur(2px)',
                        backgroundColor: 'var(--primary-mask)'
                    }
                }}
                footer={[
                    <Button key="cancel" onClick={handleCancel}>
                        Hủy
                    </Button>,
                    <Button key="sign" type="primary" loading={loading} onClick={handleSign}>
                        Ký hợp đồng
                    </Button>,
                ]}
            >
                <div className="flex flex-row w-full items-center justify-start select-none mb-4">
                    <p className="ml-2 text-2xl font-bold text-primary">KÝ HỢP ĐỒNG ĐIỆN TỬ</p>
                </div>
                <div className="flex flex-row justify-center mt-6">
                    <Form<SignDocumentModel>
                        name="signDocument"
                        className="flex flex-col items-end"
                        form={form}
                        layout="vertical"
                    >
                        <div className="grid grid-cols-1 gap-x-4 items-end">
                            <Form.Item<SignDocumentModel>
                                label="Loại OTP"
                                name="otpType"
                                rules={[{ required: true, message: 'Vui lòng chọn loại OTP!' }]}
                                initialValue={1}
                            >
                                <Select placeholder="Chọn loại OTP" style={{width: INPUT_WIDTH}}>
                                    <Select.Option value={1}>SMS</Select.Option>
                                    <Select.Option value={2}>Email</Select.Option>
                                </Select>
                            </Form.Item>
                            <Form.Item<SignDocumentModel>
                                label="Email/Số điện thoại"
                                name="deviceOTP"
                                rules={[
                                    { required: true, message: 'Vui lòng nhập email hoặc số điện thoại!' },
                                    { type: 'email', message: 'Vui lòng nhập đúng định dạng email!' }
                                ]}
                            >
                                <Input 
                                    placeholder="Nhập email hoặc số điện thoại" 
                                    style={{width: INPUT_WIDTH}}
                                ></Input>
                            </Form.Item>
                            <Form.Item<SignDocumentModel>
                                label="Mã OTP"
                                name="codeOTP"
                                rules={[
                                    { required: true, message: 'Vui lòng nhập mã OTP!' }
                                ]}
                            >
                                <Input 
                                    placeholder="Nhập mã OTP" 
                                    style={{width: INPUT_WIDTH}}
                                ></Input>
                            </Form.Item>
                            <Form.Item<SignDocumentModel>
                                label="Chữ ký khách hàng (Base64, tùy chọn)"
                                name="signatureCustomerImage64"
                                help="Ảnh chữ ký của khách hàng dạng Base64"
                            >
                                <Input.TextArea 
                                    placeholder="Nhập chuỗi Base64 của ảnh chữ ký" 
                                    style={{width: INPUT_WIDTH}}
                                    rows={3}
                                ></Input.TextArea>
                            </Form.Item>
                            <div className="text-sm text-gray-500 mt-2" style={{width: INPUT_WIDTH}}>
                                Vui lòng nhập đầy đủ thông tin để ký hợp đồng điện tử.
                            </div>
                        </div>
                    </Form>
                </div>
            </Modal>
        </ConfigProvider>
    );
};
